import React, { useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Plus, Loader2, Link as LinkIcon, Image, X } from 'lucide-react';
import { toast } from 'sonner';
import { TagSelector } from '../TagBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'general', label: 'General' },
  { value: 'care-tips', label: 'Care Tips' },
  { value: 'identification', label: 'Breed ID' },
  { value: 'showcase', label: 'Showcase' },
  { value: 'question', label: 'Question' },
  { value: 'news', label: 'News' }
];

export function CreatePostDialog({ open, onOpenChange, onPostCreated }) {
  const [creating, setCreating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [fetchingPreview, setFetchingPreview] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [showDiscardAlert, setShowDiscardAlert] = useState(false);
  const fileInputRef = useRef(null);
  const [newPost, setNewPost] = useState({
    title: '',
    content: '',
    category: 'general',
    image_url: '',
    tags: [],
    link_preview: null
  });

  // Extract URLs from text
  const extractUrl = (text) => {
    const urlPattern = /https?:\/\/[^\s<>"{}|\\^`[\]]+/g;
    const matches = text.match(urlPattern);
    return matches ? matches[0] : null;
  };

  // Fetch link preview when URL is detected
  const fetchLinkPreview = useCallback(async (url) => {
    if (!url) return;
    setFetchingPreview(true);
    try {
      const response = await axios.get(`${API}/link-preview/fetch`, {
        params: { url }
      });
      setNewPost(prev => ({ ...prev, link_preview: response.data }));
    } catch (error) {
      console.log('Could not fetch link preview:', error.response?.data?.detail || error.message);
    } finally {
      setFetchingPreview(false);
    }
  }, []);

  // Handle content change and detect URLs
  const handleContentChange = (content) => {
    setNewPost(prev => ({ ...prev, content }));
    
    const url = extractUrl(content);
    if (url && !newPost.link_preview) {
      fetchLinkPreview(url);
    } else if (!url && newPost.link_preview) {
      setNewPost(prev => ({ ...prev, link_preview: null }));
    }
  };

  // Handle image upload
  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
    
    // Allow up to 10MB - server will compress if needed
    if (file.size > 10 * 1024 * 1024) {
      toast.error(`Image too large (${fileSizeMB}MB). Maximum 10MB allowed.`);
      return;
    }

    // Show preview immediately
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);

    // Upload to server
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/uploads/screenshot`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
      setNewPost(prev => ({ ...prev, image_url: imageUrl }));
      
      // Show compression info if image was compressed
      if (response.data.compressed) {
        toast.success(`Image uploaded & compressed (${response.data.compression_ratio}x smaller)`);
      } else {
        toast.success('Image uploaded');
      }
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to upload image';
      toast.error(`${detail}`);
      setImagePreview(null);
    } finally {
      setUploading(false);
    }
  };

  const clearImage = () => {
    setImagePreview(null);
    setNewPost(prev => ({ ...prev, image_url: '' }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleCreatePost = async () => {
    if (!newPost.title.trim() || !newPost.content.trim()) {
      toast.error('Please fill in title and content');
      return;
    }
    setCreating(true);
    try {
      const response = await axios.post(`${API}/posts`, newPost);
      setNewPost({ title: '', content: '', category: 'general', image_url: '', tags: [], link_preview: null });
      onOpenChange(false);
      onPostCreated?.(response.data);
      toast.success('Post created!');
    } catch (error) {
      toast.error('Failed to create post');
    } finally {
      setCreating(false);
    }
  };

  const resetForm = () => {
    setNewPost({ title: '', content: '', category: 'general', image_url: '', tags: [], link_preview: null });
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const hasUnsavedContent = newPost.title.trim() || newPost.content.trim() || imagePreview;

  const handleDialogClose = (isOpen) => {
    if (!isOpen && hasUnsavedContent) {
      setShowDiscardAlert(true);
      return;
    }
    if (!isOpen) resetForm();
    onOpenChange(isOpen);
  };

  const handleDiscard = () => {
    setShowDiscardAlert(false);
    resetForm();
    onOpenChange(false);
  };

  return (
    <>
    <Dialog open={open} onOpenChange={handleDialogClose}>
      <DialogTrigger asChild>
        <Button className="btn-primary" data-testid="create-post-btn">
          <Plus className="w-4 h-4 mr-2" />
          New Post
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">Create a Post</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <Input
            placeholder="Title"
            value={newPost.title}
            onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
            data-testid="post-title-input"
            className="input-field"
          />
          <div className="relative">
            <Textarea
              placeholder="Share your thoughts, tips, or questions... Paste a link to auto-generate a preview!"
              value={newPost.content}
              onChange={(e) => handleContentChange(e.target.value)}
              rows={4}
              data-testid="post-content-input"
              className="resize-none"
            />
            {fetchingPreview && (
              <div className="absolute right-2 top-2">
                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
              </div>
            )}
          </div>

          {/* Link Preview */}
          {newPost.link_preview && (
            <div className="relative">
              <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                <LinkIcon className="w-3 h-3" />
                Link preview detected
              </div>
              <div className="border rounded-lg p-3 bg-muted/50">
                <div className="flex gap-3">
                  {newPost.link_preview.image && (
                    <img 
                      src={newPost.link_preview.image} 
                      alt="" 
                      className="w-20 h-20 object-cover rounded"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm line-clamp-1">{newPost.link_preview.title}</p>
                    <p className="text-xs text-muted-foreground line-clamp-2">{newPost.link_preview.description}</p>
                    <p className="text-xs text-primary mt-1">{newPost.link_preview.domain}</p>
                  </div>
                </div>
              </div>
              <button
                onClick={() => setNewPost(prev => ({ ...prev, link_preview: null }))}
                className="absolute top-0 right-0 text-xs text-muted-foreground hover:text-foreground"
              >
                Remove
              </button>
            </div>
          )}

          <div className="flex gap-4 items-start">
            <Select
              value={newPost.category}
              onValueChange={(val) => setNewPost({ ...newPost, category: val })}
            >
              <SelectTrigger className="w-40" data-testid="post-category-select">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Image Upload */}
          <div>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageSelect}
              ref={fileInputRef}
              className="hidden"
              id="post-image-upload"
            />
            {imagePreview ? (
              <div className="relative">
                <img src={imagePreview} alt="Preview" className="w-full h-48 object-cover rounded-lg" />
                <button
                  onClick={clearImage}
                  className="absolute top-2 right-2 p-1 bg-black/50 rounded-full hover:bg-black/70"
                  disabled={uploading}
                >
                  <X className="w-4 h-4 text-white" />
                </button>
                {uploading && (
                  <div className="absolute inset-0 bg-black/30 flex items-center justify-center rounded-lg">
                    <Loader2 className="w-6 h-6 animate-spin text-white" />
                  </div>
                )}
              </div>
            ) : (
              <label
                htmlFor="post-image-upload"
                className="flex items-center justify-center gap-2 p-4 border-2 border-dashed rounded-lg cursor-pointer hover:bg-muted/50 transition-colors"
                title="Images are automatically compressed to fit. Max 10MB upload."
              >
                <Image className="w-5 h-5 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Add image</span>
                <span className="text-xs text-muted-foreground/70">(auto-compressed)</span>
              </label>
            )}
          </div>

          {/* Tag Selector */}
          <TagSelector
            selectedTags={newPost.tags}
            onChange={(tags) => setNewPost({ ...newPost, tags })}
          />

          <Button 
            className="w-full btn-primary" 
            onClick={handleCreatePost}
            disabled={creating}
            data-testid="submit-post-btn"
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Publish Post
          </Button>
        </div>
      </DialogContent>
    </Dialog>

    <AlertDialog open={showDiscardAlert} onOpenChange={setShowDiscardAlert}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Discard post?</AlertDialogTitle>
          <AlertDialogDescription>
            You have unsaved changes. Are you sure you want to discard this post?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="continue-editing-btn">Continue Editing</AlertDialogCancel>
          <AlertDialogAction onClick={handleDiscard} className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            data-testid="discard-post-btn">
            Discard
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}

export default CreatePostDialog;
